// Keyboard navigation, user-rebindable.
//
// A binding is either a two-key sequence ("g t") or a modifier chord
// ("ctrl+shift+t"). Sequences are the default because nearly every Ctrl-letter
// is claimed — by the browser (Ctrl+T, Ctrl+N, Ctrl+P, which the invoice view
// needs) or by this app (Ctrl+D fill-down, Ctrl+K assistant) — so a sequence
// collides with nothing.
//
// The tradeoff: bare letters are only safe while no field has focus, which in
// this app is rare. A chord has no such restriction and fires mid-sentence,
// which is why chords are worth offering even though most are spoken for.

import { navigate } from './router'

export interface Binding {
  /** Stable identity: a screen name, or `client:<id>`. */
  id: string
  label: string
  to: string
  /**
   * Either a single chord — "ctrl+shift+t", "alt+arrowup" — or a two-step
   * sequence of plain keys, space separated: "g t".
   */
  keys: string
}

/** Normalised description of a keystroke, or null for a bare modifier press. */
export function describeKey(e: KeyboardEvent): string | null {
  if (['Shift', 'Control', 'Alt', 'Meta'].includes(e.key)) return null
  const parts: string[] = []
  if (e.ctrlKey) parts.push('ctrl')
  if (e.altKey) parts.push('alt')
  if (e.metaKey) parts.push('meta')
  if (e.shiftKey) parts.push('shift')
  parts.push(e.key.toLowerCase()) // 'k', 'arrowup', 'enter', '/'
  return parts.join('+')
}

/** A chord carries ctrl/alt/meta and fires on its own; anything else is one
 *  step of a sequence. Shift alone doesn't count — Shift+/ is just "?". */
export function isChord(step: string): boolean {
  return /(^|\+)(ctrl|alt|meta)\+/.test(step)
}

const KEY_LABELS: Record<string, string> = {
  arrowup: '↑',
  arrowdown: '↓',
  arrowleft: '←',
  arrowright: '→',
  ' ': 'Space',
  escape: 'Esc',
  enter: '↵',
  backspace: '⌫',
  ctrl: 'Ctrl',
  alt: 'Alt',
  meta: 'Meta',
  shift: 'Shift',
}

/** Human-readable chips for one step, e.g. "ctrl+shift+t" -> Ctrl Shift T */
export function keyChips(step: string): string[] {
  return step.split('+').map((p) => KEY_LABELS[p] ?? (p.length === 1 ? p.toUpperCase() : p))
}

/** Shortcuts this app or the browser already relies on. Bindable, but worth a
 *  warning — Ctrl+B in a note is bold, Ctrl+D in the grid is fill-down. */
export const RESERVED: Record<string, string> = {
  'ctrl+k': 'opens the assistant',
  'ctrl+d': 'fill down in the grid',
  'ctrl+backspace': 'delete row in the grid',
  'ctrl+b': 'bold in notes',
  'ctrl+i': 'italic in notes',
  'ctrl+u': 'underline in notes',
  'ctrl+p': 'print — the invoice view needs it',
  'ctrl+t': 'new browser tab',
  'ctrl+n': 'new browser window',
  'ctrl+w': 'close tab',
  'ctrl+f': 'browser find',
}

export const DEFAULT_BINDINGS: Binding[] = [
  { id: 'dashboard', label: 'Dashboard', to: '#/', keys: 'g d' },
  { id: 'today', label: 'Today', to: '#/today', keys: 'g t' },
  { id: 'todos', label: 'To-Do', to: '#/todos', keys: 'g k' },
  { id: 'notes', label: 'Notes', to: '#/notes', keys: 'g n' },
  { id: 'chat', label: 'Chat', to: '#/chat', keys: 'g h' },
  { id: 'clients', label: 'Clients', to: '#/clients', keys: 'g c' },
  { id: 'settings', label: 'Settings', to: '#/settings', keys: 'g s' },
]

const STORE_KEY = 'shortcuts:v1'

// Bindings live in localStorage rather than D1: they're a per-keyboard
// preference, and shortcuts only apply on a device that has one.
function load(): Binding[] {
  try {
    const raw = JSON.parse(localStorage.getItem(STORE_KEY) || 'null') as { bindings?: Binding[] } | null
    if (!raw?.bindings?.length) return DEFAULT_BINDINGS
    // Earlier builds stored a sequence as two bare characters ("gt"); steps are
    // now space-separated so a step can carry modifiers or a named key.
    const stored = raw.bindings.map((b) =>
      b.keys && !b.keys.includes(' ') && !b.keys.includes('+') && b.keys.length === 2
        ? { ...b, keys: `${b.keys[0]} ${b.keys[1]}` }
        : b,
    )
    const missing = DEFAULT_BINDINGS.filter((d) => !stored.some((s) => s.id === d.id))
    return [...stored, ...missing]
  } catch {
    return DEFAULT_BINDINGS
  }
}

let bindings: Binding[] = load()
const listeners = new Set<() => void>()

export function getBindings(): Binding[] {
  return bindings
}
export function subscribeBindings(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function persist() {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify({ bindings }))
  } catch {
    /* private mode — bindings just won't survive a reload */
  }
  for (const l of listeners) l()
}

/** Assign keys to a binding. Any other binding holding those keys loses them,
 *  so a duplicate can't shadow one silently. */
export function setKeys(id: string, keys: string) {
  bindings = bindings.map((b) =>
    b.id === id ? { ...b, keys } : b.keys === keys ? { ...b, keys: '' } : b,
  )
  persist()
}

export function addClientBinding(clientId: number, label: string, keys: string) {
  const id = `client:${clientId}`
  const rest = bindings.filter((b) => b.id !== id).map((b) => (b.keys === keys ? { ...b, keys: '' } : b))
  bindings = [...rest, { id, label, to: `#/client/${clientId}`, keys }]
  persist()
}

export function removeBinding(id: string) {
  bindings = bindings.filter((b) => b.id !== id)
  persist()
}

export function resetBindings() {
  bindings = DEFAULT_BINDINGS
  persist()
}

/** Keep client labels current if a client is renamed or removed. */
export function syncClientLabels(clients: { id: number; simple_name: string }[]) {
  let changed = false
  const next = bindings.flatMap((b) => {
    if (!b.id.startsWith('client:')) return [b]
    const cid = Number(b.id.slice('client:'.length))
    const client = clients.find((c) => c.id === cid)
    if (!client) {
      changed = true
      return []
    }
    if (client.simple_name !== b.label) {
      changed = true
      return [{ ...b, label: client.simple_name }]
    }
    return [b]
  })
  if (changed) {
    bindings = next
    persist()
  }
}

/** True when the keystroke belongs to whatever the user is editing. */
export function isTyping(): boolean {
  const el = document.activeElement as HTMLElement | null
  if (!el) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
}

const SEQUENCE_MS = 1500

/** Installs the global handler. `onHelp` toggles the shortcut window — a
 *  chorded shortcut nobody can discover is worse than none. */
export function installShortcuts(onHelp: () => void): () => void {
  let prev: string | null = null
  let timer: ReturnType<typeof setTimeout> | null = null

  const clear = () => {
    prev = null
    if (timer) clearTimeout(timer)
    timer = null
  }

  const onKeyDown = (e: KeyboardEvent) => {
    const step = describeKey(e)
    if (!step) return // bare modifier press

    // A chord is unambiguous even mid-sentence, so it works while typing —
    // that's the point of using one. Plain sequences never fire in a field.
    if (isChord(step)) {
      const hit = bindings.find((b) => b.keys === step)
      clear()
      if (hit) {
        e.preventDefault()
        navigate(hit.to)
      }
      return
    }

    if (isTyping()) {
      clear()
      return
    }

    // e.key is already the shifted character, so Shift+/ arrives as "shift+?".
    if (step === '?' || step === 'shift+?') {
      e.preventDefault()
      clear()
      onHelp()
      return
    }

    if (prev) {
      const combo = `${prev} ${step}`
      const hit = bindings.find((b) => b.keys === combo)
      clear()
      if (hit) {
        e.preventDefault()
        navigate(hit.to)
        return
      }
      // Not a pair — fall through and treat this as a new first step.
    }

    prev = step
    timer = setTimeout(clear, SEQUENCE_MS)
  }

  window.addEventListener('keydown', onKeyDown)
  return () => {
    clear()
    window.removeEventListener('keydown', onKeyDown)
  }
}
